import { ProgramCodes, PromoCodes, VerificationProcess } from '@mesa-labs/mesa-api/dist/types';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export enum ExtraDemoCodes {
  NET30_AND_POA = 'Net30AndPoa',
  NET15_AND_POA_BEELINE = 'Net15AndPoaBeeline',
  DIRECT_DEBIT_BUILT_POA = 'DirectDebitBuiltPoa',
  DIRECT_DEBIT_SERVICE_TITAN_POA = 'DirectDebitServiceTitanPoa',
  DIRECT_DEBIT_CORRIGO_POA = 'DirectDebitCorrigoPoa',
  DIRECT_DEBIT_DIRECT_COMMERCE_POA = 'DirectDebitDirectCommercePoa',
  DIRECT_DEBIT_NET_30 = 'DirectDebitNet30',
  DIRECT_DEBIT_POA = 'DirectDebitPoa',
  DIRECT_DEBIT_ITRADE_POA = 'DirectDebitITradePoa',
  DIRECT_DEBIT_WORKDAY_VNDLY_POA = 'DirectDebitWorkdayVndlyPoa',
  DIRECT_DEBIT_GHX_NET30 = 'DirectDebitGhxNet30',
  DIRECT_DEBIT_TUNGSTEN_AUTOMATION_ON_DEMAND = 'DirectDebitTungstenAutomationOnDemand',
  TAPFIN_NET30 = 'TapfinNet30',
  KELLY_OCG_NET30 = 'KellyOcgNet30',
  AGS_NET30 = 'AgsNet30',
  RANDSTAD_NET30 = 'RandstadNet30',
  DIRECT_DEBIT_OD = 'DirectDebitOd',
}
export type DemoModeCodes = ProgramCodes | ExtraDemoCodes;
export type DemoNavigationTarget = 'onboarding' | 'dashboard';

export interface DemoModeState {
  isEnabled: boolean;
  demoModeCode: DemoModeCodes;
  hasApprovedW9: boolean;
  plaidSuccessful: boolean;
  hasAcceptedCurrentMsa: boolean;
  hasAcceptedInitialProgram: boolean;
  overrideFinished: boolean;
  hideSidebar: boolean;
  demoModeLogoSrc: string | undefined;
  hasAgreedToDirectDebitAgreement: boolean;
  hasVerifiedBankAccount: boolean;
  hasApprovedBankVerificationDocument: boolean;
  hasAcceptedDataSharingAgreement: boolean;
  hasCompletedOnboarding: boolean;
  bankVerificationProcess: VerificationProcess | undefined;
}

export const initialState: DemoModeState = {
  isEnabled: false,
  demoModeCode: ProgramCodes.NET30,
  hasApprovedW9: false,
  plaidSuccessful: false,
  hasAcceptedCurrentMsa: false,
  hasAcceptedInitialProgram: false,
  overrideFinished: false,
  hideSidebar: false,
  demoModeLogoSrc: undefined,
  hasAgreedToDirectDebitAgreement: false,
  hasVerifiedBankAccount: false,
  hasApprovedBankVerificationDocument: false,
  hasAcceptedDataSharingAgreement: false,
  hasCompletedOnboarding: false,
  bankVerificationProcess: undefined,
};

const demoModeSlice = createSlice({
  name: 'demoMode',
  initialState,
  reducers: {
    updateIsEnabled(state: DemoModeState, action: PayloadAction<boolean>) {
      state.isEnabled = action.payload;
    },
    updateDemoModeCode(state: DemoModeState, action: PayloadAction<DemoModeCodes>) {
      state.demoModeCode = action.payload;
    },
    updateHasApprovedW9(state: DemoModeState, action: PayloadAction<boolean>) {
      state.hasApprovedW9 = action.payload;
    },
    updateDemoPlaidSuccessful(state: DemoModeState, action: PayloadAction<boolean>) {
      state.plaidSuccessful = action.payload;
    },
    updateHasAcceptedCurrentMsa(state: DemoModeState, action: PayloadAction<boolean>) {
      state.hasAcceptedCurrentMsa = action.payload;
    },
    updateHasAcceptedInitialProgram(state: DemoModeState, action: PayloadAction<boolean>) {
      state.hasAcceptedInitialProgram = action.payload;
    },
    updateOverrideFinished(state: DemoModeState, action: PayloadAction<boolean>) {
      state.overrideFinished = action.payload;
    },
    updateHideSidebar(state: DemoModeState, action: PayloadAction<boolean>) {
      state.hideSidebar = action.payload;
    },
    resetDemoMode() {
      return initialState;
    },
    updateDemoModeLogoSrc(state: DemoModeState, action: PayloadAction<string | undefined>) {
      state.demoModeLogoSrc = action.payload;
    },
    updateHasAgreedToDirectDebitAgreement(state: DemoModeState, action: PayloadAction<boolean>) {
      state.hasAgreedToDirectDebitAgreement = action.payload;
    },
    updateHasVerifiedBankAccount(state: DemoModeState, action: PayloadAction<boolean>) {
      state.hasVerifiedBankAccount = action.payload;
    },
    updateHasApprovedBankVerificationDocument(state: DemoModeState, action: PayloadAction<boolean>) {
      state.hasApprovedBankVerificationDocument = action.payload;
    },
    updateHasAcceptedDataSharingAgreement(state: DemoModeState, action: PayloadAction<boolean>) {
      state.hasAcceptedDataSharingAgreement = action.payload;
    },
    completeDemoOnboarding(
      state: DemoModeState,
      action: PayloadAction<{ promoCode: PromoCodes | undefined; verificationProcess: VerificationProcess }>,
    ) {
      // Direct debit demos finish their setup in the dashboard sidebar, not in /onboarding.
      if (action.payload.promoCode === PromoCodes.DIRECT_DEBIT) {
        return;
      }

      state.hasCompletedOnboarding = true;
      state.hasAcceptedInitialProgram = true;
      state.hasAcceptedCurrentMsa = true;
      state.bankVerificationProcess = action.payload.verificationProcess;
      state.plaidSuccessful = action.payload.verificationProcess === VerificationProcess.INSTANT;
    },
  },
  extraReducers: (builder) => {
    builder.addCase('SIGN_OUT', () => initialState);
  },
});

export const {
  updateIsEnabled,
  updateDemoModeCode,
  updateHasApprovedW9,
  updateDemoPlaidSuccessful,
  resetDemoMode,
  updateHasAcceptedCurrentMsa,
  updateHasAcceptedInitialProgram,
  updateOverrideFinished,
  updateHideSidebar,
  updateDemoModeLogoSrc,
  updateHasAgreedToDirectDebitAgreement,
  updateHasVerifiedBankAccount,
  updateHasApprovedBankVerificationDocument,
  updateHasAcceptedDataSharingAgreement,
  completeDemoOnboarding,
} = demoModeSlice.actions;
export default demoModeSlice.reducer;
